import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LegalFooter } from "@/components/LegalFooter";
import axios from "axios";
import { ArrowRight, Check, Loader2, CalendarX, MessagesSquare, UserMinus, Plus } from "lucide-react";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { LandingMockup } from "@/components/LandingMockup";
import { PortalScreen, AlertScreen, CmiScreen, ConsultorMini, GestaoMini, DirecaoMini } from "@/components/landing/Screens";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Parallax, Reveal } from "@/components/landing/Parallax";
import { MotionConfig } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NAVY = "#0E2550";
const MINT = "#14B88F";
const CONTACT = "suporte@imodesk.pt";
const wrap = "mx-auto max-w-6xl px-5 sm:px-8";
const eyebrow = "text-[12px] font-bold uppercase tracking-[0.18em]";
const h2 = "text-2xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]";
const sec = "py-14 sm:py-24";

const go = (id) => () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

function PrimaryBtn({ tid, className = "", children = "Pedir demonstração", onClick = go("demonstracao"), light = false }) {
  return (
    <button onClick={onClick} data-testid={tid}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold transition-[filter,transform] hover:brightness-110 active:scale-[0.98] ${light ? "bg-white" : "text-white"} ${className}`}
      style={light ? { color: NAVY } : { background: NAVY }}>
      {children} <ArrowRight size={16} />
    </button>
  );
}

function Mark({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" aria-hidden="true">
      <path d="M8 24 L24 10 L40 24" stroke={NAVY} strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 35 H33" stroke={MINT} strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

const Brand = ({ tid = "landing-logo" }) => (
  <Link to="/" className="inline-flex items-center gap-2 shrink-0" data-testid={tid}>
    <Mark /><span className="text-[20px] sm:text-[22px] font-extrabold tracking-tight" style={{ color: NAVY }}>Imo<span style={{ color: MINT }}>Desk</span></span>
  </Link>
);

function Header({ home = true }) {
  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-md border-b border-slate-100">
      <div className={`${wrap} h-16 flex items-center justify-between gap-6`}>
        <Brand />
        <nav className="flex items-center gap-3 sm:gap-6">
          <Link to="/entrar" className="px-2 py-2 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="landing-login">Entrar</Link>
          {home ? (
            <button onClick={go("demonstracao")} className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-110" style={{ background: NAVY }} data-testid="landing-demo-top">
              <span className="sm:hidden">Demonstração</span><span className="hidden sm:inline">Pedir demonstração</span>
            </button>
          ) : (
            <Link to="/#demonstracao" className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-110" style={{ background: NAVY }} data-testid="landing-demo-top">
              <span className="sm:hidden">Demonstração</span><span className="hidden sm:inline">Pedir demonstração</span>
            </Link>
          )}
        </nav>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className={`${wrap} pt-10 sm:pt-20 pb-14 sm:pb-24 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center`} data-testid="landing-hero">
      <div className="animate-in fade-in slide-in-from-bottom-3 duration-700">
        <div className={eyebrow} style={{ color: MINT }}>Para agências imobiliárias</div>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.04]" style={{ color: NAVY }} data-testid="landing-title">
          Da angariação à escritura, sem nada esquecido.
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl" data-testid="landing-subtitle">
          Documentos recolhidos sem esforço, contrato de mediação pronto a assinar e processos organizados para fechar a escritura no prazo.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <PrimaryBtn tid="landing-demo-hero" />
          <button onClick={go("como-funciona")} className="inline-flex items-center justify-center rounded-full px-6 py-3 text-[15px] font-semibold border border-slate-200 hover:border-slate-300 hover:bg-slate-50 transition-colors" style={{ color: NAVY }} data-testid="landing-how-btn">Ver como funciona</button>
        </div>
        <p className="mt-6 text-sm text-slate-500" data-testid="landing-made-in">Feito em Portugal, para a forma como as agências portuguesas trabalham.</p>
      </div>
      <div className="animate-in fade-in slide-in-from-bottom-6 duration-1000 lg:pl-4"><Parallax speed={90} tid="parallax-hero"><LandingMockup /></Parallax></div>
    </section>
  );
}

const PROBLEMS = [
  [CalendarX, "Validades esquecidas:", "O cartão de cidadão ou certidão caduca e o notário adia a escritura no próprio dia."],
  [MessagesSquare, "Falta de organização:", "Os documentos chegam soltos pelo WhatsApp, e-mail e SMS — ninguém sabe o que realmente falta."],
  [UserMinus, "Perda de controlo:", "O consultor sai da agência e o histórico dos processos vai todo com ele."],
];

function Problem() {
  return (
    <section className={`bg-slate-50 border-y border-slate-100 ${sec}`} data-testid="landing-problem">
      <div className={`${wrap} grid lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-16 items-start`}>
        <div>
          <div className={eyebrow} style={{ color: MINT }}>O problema</div>
          <h2 className={`${h2} mt-3`} style={{ color: NAVY }}>O caos da documentação custa tempo e vendas à agência.</h2>
        </div>
        <ul className="divide-y divide-slate-200 border-y border-slate-200">
          {PROBLEMS.map(([Icon, lead, t], i) => (
            <Reveal as="li" delay={i * 0.12} key={lead} className="flex items-start gap-4 py-4 sm:py-5" data-testid={`landing-problem-${i + 1}`}>
              <span className="w-10 h-10 rounded-full bg-white border border-slate-200 grid place-items-center shrink-0"><Icon size={18} strokeWidth={1.75} style={{ color: NAVY }} /></span>
              <p className="text-base sm:text-lg leading-snug text-slate-600 pt-1.5"><strong className="font-bold" style={{ color: NAVY }}>{lead}</strong> {t}</p>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

const CHANGES = [
  ["O cliente envia tudo por um link.", "Sem instalar nada, a partir do telemóvel, com a marca da sua agência.", PortalScreen, "portal"],
  ["O ImoDesk lê os documentos e avisa antes do notário.", "Validades, dados que não batem certo, páginas em falta.", AlertScreen, "avisos"],
  ["O contrato de mediação sai preenchido em segundos.", "Com os dados dos documentos, pronto a rever e enviar.", CmiScreen, "cmi"],
];

function Changes() {
  return (
    <section id="como-funciona" className={`${wrap} ${sec} scroll-mt-16`} data-testid="landing-changes">
      <div className={eyebrow} style={{ color: MINT }}>O que muda</div>
      <h2 className={`${h2} mt-3 max-w-2xl`} style={{ color: NAVY }}>Menos telefonemas, menos papéis perdidos.</h2>
      <div className="mt-10 sm:mt-14 space-y-12 sm:space-y-20">
        {CHANGES.map(([t, d, Shot, id], i) => (
          <div key={id} className={`grid md:grid-cols-2 gap-6 md:gap-14 items-center`} data-testid={`landing-change-${id}`}>
            <Reveal className={i % 2 ? "md:order-2" : ""}>
              <span className="text-[13px] font-bold" style={{ color: MINT }}>0{i + 1}</span>
              <h3 className="mt-2 text-xl sm:text-2xl font-bold leading-snug" style={{ color: NAVY }}>{t}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
            </Reveal>
            <div className={`${i % 2 ? "md:order-1" : ""} max-w-md w-full mx-auto md:mx-0`}><Parallax speed={i % 2 ? 150 : 110} tid={`parallax-change-${i + 1}`}><Shot /></Parallax></div>
          </div>
        ))}
      </div>
    </section>
  );
}

const ROLES = [
  ["Consultor", "Sabe o que tem de fazer hoje e quanto vai ganhar. O CMI sai pronto e os lembretes aos clientes saem com um toque.", ConsultorMini],
  ["Gestão processual", "Vê todos os processos até à escritura e o que está à espera de quem. Deixa de andar atrás dos consultores.", GestaoMini],
  ["Direção", "Acompanha angariações, escrituras e comissões. E os processos ficam na agência, mesmo quando um consultor sai.", DirecaoMini],
];

function Roles() {
  return (
    <section className={`bg-slate-50 border-b border-slate-100 ${sec}`} data-testid="landing-who">
      <div className={wrap}>
        <div className={eyebrow} style={{ color: MINT }}>Para cada pessoa da agência</div>
        <h2 className={`${h2} mt-3 max-w-2xl`} style={{ color: NAVY }}>Cada um vê o que precisa.</h2>
        <div className="mt-10 grid md:grid-cols-3 gap-8 md:gap-6">
          {ROLES.map(([t, d, Shot], i) => (
            <Reveal as="article" delay={i * 0.12} key={t} data-testid={`landing-who-${t.split(" ")[0].toLowerCase()}`}>
              <div className="md:h-[300px] flex items-center"><Parallax speed={70} className="w-full"><Shot /></Parallax></div>
              <h3 className="mt-5 text-lg font-bold" style={{ color: NAVY }}>{t}</h3>
              <p className="mt-1.5 text-slate-600 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const TRUST = [
  ["whatsapp", "WhatsApp Business oficial", "Mensagens enviadas pela API oficial da Meta, sem números pessoais."],
  ["cmd", "Preparado para Chave Móvel Digital", "Contrato de mediação e Anexo B prontos a assinar digitalmente ou em papel."],
  ["amlr", "Pensado para AMLR e IMPIC", "Recolha de KYC, registo de diligências e apoio às comunicações trimestrais."],
];

const TrustLogo = ({ id }) => {
  if (id === "whatsapp") return <span className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: "#E7F8EE" }}><WhatsAppIcon size={24} /></span>;
  if (id === "cmd") return <span className="w-12 h-12 rounded-xl grid place-items-center" style={{ background: NAVY }}><img src="/logos/autenticacao-gov-simbolo.svg" alt="Autenticação.gov · Chave Móvel Digital" className="w-8 h-8" data-testid="landing-trust-logo-cmd" /></span>;
  return <img src="/logos/impic.png" alt="IMPIC · Instituto dos Mercados Públicos, do Imobiliário e da Construção" className="h-12 w-auto" data-testid="landing-trust-logo-impic" />;
};

function Trust() {
  return (
    <section className={`bg-slate-50 border-t border-slate-100 ${sec}`} data-testid="landing-trust">
      <div className={`${wrap} text-center`}>
        <div className={eyebrow} style={{ color: MINT }}>Segurança e conformidade</div>
        <h2 className={`${h2} mt-3`} style={{ color: NAVY }}>Feito para as regras da mediação em Portugal</h2>
        <p className="mt-4 max-w-2xl mx-auto text-base sm:text-lg text-slate-600 leading-relaxed">Documentos, assinaturas e diligências tratados com as ferramentas oficiais, sem números pessoais nem papéis perdidos.</p>
        <div className="mt-10 sm:mt-14 grid md:grid-cols-3 gap-5 sm:gap-8 text-left">
          {TRUST.map(([id, t, d], i) => (
            <Reveal as="article" delay={i * 0.12} key={id} className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 transition-shadow duration-200 hover:shadow-md" data-testid={`landing-trust-${id}`}>
              <TrustLogo id={id} />
              <h3 className="mt-6 text-lg sm:text-xl font-bold leading-snug" style={{ color: NAVY }}>{t}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const FAQ = [
  ["Substitui o nosso CRM?", "Não. O CRM trata dos imóveis e contactos; o ImoDesk trata de tudo o que pode adiar a escritura."],
  ["Os clientes têm de instalar alguma coisa?", "Não. Recebem um link e enviam os documentos pelo telemóvel."],
  ["Funciona com empresas e heranças?", "Sim. Particulares, casais, empresas e heranças: o ImoDesk sabe que documentos pedir a cada um."],
  ["E se a leitura automática se enganar?", "O documento fica «a verificar» até alguém da equipa confirmar. A decisão é sempre da agência."],
  ["Os dados estão seguros e são nossos?", "Sim. Cada agência só vê os seus dados, os documentos só abrem para quem trabalha no processo e pode exportá-los a qualquer momento."],
];

function Faq() {
  return (
    <section id="perguntas" className={`${sec}`} data-testid="landing-faq">
      <div className={`${wrap} max-w-3xl`}>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: NAVY }} data-testid="faq-title">Perguntas frequentes</h1>
        <Accordion type="single" collapsible className="mt-8 border-t border-slate-200">
        {FAQ.map(([q, a], i) => (
          <AccordionItem key={q} value={`q${i}`} className="border-slate-200">
            <AccordionTrigger className="text-left text-[15px] sm:text-base font-semibold hover:no-underline" style={{ color: NAVY }} data-testid={`faq-q-${i + 1}`}>{q}</AccordionTrigger>
            <AccordionContent className="text-slate-600 text-[15px] leading-relaxed" data-testid={`faq-a-${i + 1}`}>{a}</AccordionContent>
          </AccordionItem>
        ))}
        </Accordion>
      </div>
    </section>
  );
}

const EMPTY = { name: "", agency: "", phone: "", email: "", message: "", privacy: false, website: "" };

function DemoForm() {
  const [f, setF] = useState(EMPTY);
  const [note, setNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const valid = f.name.trim().length > 1 && f.agency.trim().length > 1 && f.phone.replace(/\D/g, "").length >= 9 && /\S+@\S+\.\S+/.test(f.email) && f.privacy;
  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true); setErr("");
    try { await axios.post(`${API}/public/demo-request`, { ...f, message: f.message.trim() || null }); setDone(true); }
    catch (e2) { setErr(typeof e2.response?.data?.detail === "string" ? e2.response.data.detail : "Não foi possível enviar. Tente outra vez."); }
    setBusy(false);
  };
  const field = (k, label, props = {}) => (
    <label className="block"><span className="text-sm font-medium text-slate-700">{label}</span>
      <Input value={f[k]} onChange={set(k)} className="mt-1.5 h-11 bg-white" data-testid={`demo-${k}`} {...props} /></label>
  );
  return (
    <section id="demonstracao" className={`${sec} scroll-mt-16`} data-testid="landing-demo">
      <div className={`${wrap} grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12`}>
        <div>
          <div className={eyebrow} style={{ color: MINT }}>Pedir demonstração</div>
          <h2 className={`${h2} mt-3`} style={{ color: NAVY }}>Experimente 60 dias, sem custo.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md" data-testid="landing-pilot">Mostramos o ImoDesk com os processos da sua agência, configuramos a sua marca e formamos a equipa.</p>
        </div>
        {done ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-10 grid place-items-center text-center" data-testid="demo-success">
            <span className="w-12 h-12 rounded-full grid place-items-center text-white" style={{ background: MINT }}><Check size={24} strokeWidth={3} /></span>
            <p className="mt-5 text-xl font-bold" style={{ color: NAVY }}>Obrigado. Respondemos em 1 dia útil.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-8 space-y-4" data-testid="demo-form">
            <div className="grid sm:grid-cols-2 gap-4">
              {field("name", "Nome", { autoComplete: "name", required: true })}
              {field("agency", "Agência", { autoComplete: "organization", required: true })}
              {field("phone", "Telemóvel", { type: "tel", inputMode: "tel", autoComplete: "tel", required: true })}
              {field("email", "Email", { type: "email", autoComplete: "email", required: true })}
            </div>
            {note ? (
              <label className="block"><span className="text-sm font-medium text-slate-700">Nota <span className="text-slate-400 font-normal">(opcional)</span></span>
                <textarea value={f.message} onChange={set("message")} rows={3} maxLength={2000} autoFocus className="mt-1.5 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" data-testid="demo-message" /></label>
            ) : (
              <button type="button" onClick={() => setNote(true)} className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline" style={{ color: NAVY }} data-testid="demo-add-note"><Plus size={15} /> Acrescentar uma nota</button>
            )}
            <input type="text" value={f.website} onChange={set("website")} tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-2.5 text-sm text-slate-600">
              <Checkbox checked={f.privacy} onCheckedChange={(v) => setF({ ...f, privacy: !!v })} className="mt-0.5" data-testid="demo-privacy" />
              <span>Li a <Link to="/privacidade" target="_blank" className="underline font-medium" style={{ color: NAVY }}>Política de Privacidade</Link>.</span>
            </label>
            {err && <p className="text-sm text-red-700" data-testid="demo-error">{err}</p>}
            <button type="submit" disabled={!valid || busy} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[15px] font-semibold text-white disabled:opacity-40 hover:brightness-110 transition-[filter]" style={{ background: NAVY }} data-testid="demo-submit">
              {busy && <Loader2 size={16} className="animate-spin" />} Pedir demonstração
            </button>
            <p className="text-center text-sm text-slate-500" data-testid="demo-reply-note">Respondemos em 1 dia útil. Sem compromisso.</p>
          </form>
        )}
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-slate-100" data-testid="landing-footer">
      <div className={`${wrap} py-10 space-y-5 text-sm text-slate-500`}>
        <div className="flex flex-col sm:flex-row gap-5 sm:items-center justify-between">
          <Brand tid="footer-logo" />
          <nav className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/entrar" className="hover:text-slate-900" data-testid="footer-login">Entrar</Link>
            <Link to="/perguntas" className="hover:text-slate-900" data-testid="footer-faq">Perguntas frequentes</Link>
            <a href={`mailto:${CONTACT}`} className="hover:text-slate-900" data-testid="footer-contact">{CONTACT}</a>
          </nav>
        </div>
        <LegalFooter />
      </div>
    </footer>
  );
}

export default function Landing() {
  useEffect(() => {
    document.title = "ImoDesk · Da angariação à escritura";
    if (window.location.hash === "#demonstracao") {
      setTimeout(go("demonstracao"), 300);
    }
  }, []);
  return (
    <MotionConfig reducedMotion="never">
    <div className="min-h-screen bg-white text-slate-900 selection:bg-[#14B88F]/20" style={{ fontFamily: "Inter, system-ui, sans-serif" }} data-testid="landing-page">
      <Header />
      <main><Hero /><Problem /><Changes /><Roles /><DemoForm /><Trust /></main>
      <Footer />
    </div>
    </MotionConfig>
  );
}

export function FaqPage() {
  useEffect(() => { document.title = "ImoDesk · Perguntas frequentes"; window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900" style={{ fontFamily: "Inter, system-ui, sans-serif" }} data-testid="faq-page">
      <Header home={false} />
      <main><Faq /></main>
      <Footer />
    </div>
  );
}
